import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  TextField, 
  Button, 
  Paper, 
  Typography, 
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { tenderApi } from '../api/tenderApi';

const TenderForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'Open',  // Set default status to 'Open'
    deadline: '',
    image: null,
    budget: '',
    requirements: '',
    category: '',
  });
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (isEditing) {
      fetchTender();
    }
  }, [id]);

  const fetchTender = async () => {
    try {
      setLoading(true);
      const tender = await tenderApi.getTenderById(id);
      setFormData({
        ...tender,
        deadline: tender.deadline ? new Date(tender.deadline).toISOString().split('T')[0] : ''
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const formDataToSend = new FormData();
      
      // Append all form fields to FormData
      Object.keys(formData).forEach(key => {
        if (key === 'image' && formData[key]) {
          formDataToSend.append('image', formData[key]);
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (isEditing) {
        await tenderApi.updateTender(id, formDataToSend);
      } else {
        await tenderApi.createTender(formDataToSend);
      }
      navigate('/tenders');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prevData => ({
        ...prevData,
        image: file
      }));
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading && isEditing) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper className="p-6 max-w-3xl mx-auto mt-8">
      <Typography variant="h5" component="h1" className="mb-6">
        {isEditing ? 'Edit Tender' : 'Create New Tender'}
      </Typography>

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <form onSubmit={handleSubmit} className="space-y-4">
        <TextField
          fullWidth
          label="Title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
        />

        <TextField
          fullWidth
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          multiline
          rows={4}
          required
        />

        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            name="status"
            value={formData.status}
            onChange={handleChange}
            label="Status"
            required
          >
            <MenuItem value="Open">Open</MenuItem>
            <MenuItem value="Awarded">Awarded</MenuItem>
            <MenuItem value="Draft">Draft</MenuItem>
            <MenuItem value="Closed">Closed</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Deadline"
          name="deadline"
          type="date"
          value={formData.deadline}
          onChange={handleChange}
          InputLabelProps={{
            shrink: true,
          }}
          required
        />

        <TextField
          fullWidth
          label="Budget"
          name="budget"
          value={formData.budget}
          onChange={handleChange}
          type="number"
          required
        />

        <TextField
          fullWidth
          label="Requirements"
          name="requirements"
          value={formData.requirements}
          onChange={handleChange}
          multiline
          rows={4}
          required
        />

        <TextField
          fullWidth
          label="Category"
          name="category"
          value={formData.category}
          onChange={handleChange}
          required
        />

        <Box className="space-y-4">
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            style={{ display: 'none' }}
            id="tender-image-input"
          />
          <label htmlFor="tender-image-input">
            <Button
              variant="outlined"
              component="span"
              fullWidth
              startIcon={<CloudUploadIcon />}
              className="mb-2"
            >
              Upload Tender Image
            </Button>
          </label>
          
          {imagePreview && (
            <Box className="mt-2 relative">
              <img
                src={imagePreview}
                alt="Tender preview"
                className="w-full h-48 object-cover rounded-md"
              />
              <IconButton
                className="absolute top-2 right-2 bg-white hover:bg-gray-100"
                onClick={() => {
                  setFormData(prev => ({ ...prev, image: null }));
                  setImagePreview(null);
                }}
              >
                <ClearIcon/>
              </IconButton>
            </Box>
          )}
        </Box>

        <Box className="flex justify-end space-x-2 pt-4">
          <Button 
            variant="outlined" 
            onClick={() => navigate('/tenders')}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : isEditing ? (
              'Update Tender'
            ) : (
              'Create Tender'
            )}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default TenderForm;
